
import showdown from "showdown";
import { CacheManager } from "./CacheManager";
import { FormControls, i18nManager } from "../i18n";
import { SetURLSearchParams } from "react-router-dom";
import Jcb from "../assets/images/account_settings/payment_methods/jcb.png";
import Amex from "../assets/images/account_settings/payment_methods/amex.png";
import Bank from "../assets/images/account_settings/payment_methods/bank.png";
import VisaCard from "../assets/images/account_settings/payment_methods/visa.png";
import Unknown from "../assets/images/account_settings/payment_methods/unknown.png";
import Discover from "../assets/images/account_settings/payment_methods/discover.png";
import UnionPay from "../assets/images/account_settings/payment_methods/union_pay.png";
import MasterCard from "../assets/images/account_settings/payment_methods/mastercard.png";
import DinnerClub from "../assets/images/account_settings/payment_methods/dinner_club.png";
import { Checkbox, ComposedPassword, DateHelper, DateInput, DateTimeInput, DateTimePickerEvent, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, DropdownManageRef, EmailInput, NoseurObject, NumberInput, ObjectHelper, Scheme, TextAreaInput, TextInput } from "@rivtn/noseur";
import { IMember, ITeam } from "../services";

export type URLSearchParamsValue = string | string[] | number | number[] | undefined | null;

export const Utils = {

    AMOUNT_MICRO_AMOUNT: 1000000,
    MARKDOWN_CONVERTER: new showdown.Converter(),
    __TIME_KEEPER: i18nManager.Labels.time_keeping,
    _SETTINGS: CacheManager.get("omnehost.sts", {}) as NoseurObject<any>,

    signIn: () => {
        window.location = (process.env.REACT_APP_PATULCIUS_AUTH_URL ?? "http://localhost:3005/oauth") + "?ref=" + encodeURIComponent(window.location.href) + "&bid=bb48bad1-1385-403a-b7a5-a98a4c6dcbd6" as any;
    },

    randomString(length: number = 10, characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') {
        let result = '';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    },

    randomInt(min: number, max: number) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    toSaneSentenceFormat(value: string, ignore?: boolean) {
        if (ignore || !value) return value;
        return value.split("_").map(e => e[0].toUpperCase() + e.substring(1).toLowerCase()).join(" ");
    },

    fileToBase64: (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                const base64data = reader.result as string;
                if (!base64data) {
                    reject(new Error("Failed to convert File to Base64"));
                    return;
                }
                resolve(base64data!.substring(base64data!.indexOf(',') + 1));
            };
        });
    },

    updateSearchParams(key: string, value: URLSearchParamsValue, setSearchParams: SetURLSearchParams | URLSearchParams, cb?: Function | undefined) {
        const fun = (prev: URLSearchParams) => {
            if (value === undefined || value === null) {
                prev.delete(key);
            } else if (Array.isArray(value)) {
                prev.delete(key);
                for (const v of value) prev.append(key, `${v}`);
            } else {
                prev.set(key, `${value}`);
            }
            return prev;
        };
        if (setSearchParams instanceof URLSearchParams) fun(setSearchParams);
        else setSearchParams(fun);
        cb?.();
    },

    updateSearchParamses(values: NoseurObject<URLSearchParamsValue>, setSearchParams: SetURLSearchParams | URLSearchParams, cb?: () => void) {
        const entries = Object.entries(values);
        const fun = (prev: URLSearchParams) => {
            for (const [key, value] of entries) {
                if (value === undefined || value === null) {
                    prev.delete(key);
                } else if (Array.isArray(value)) {
                    prev.delete(key);
                    for (const v of value) prev.append(key, `${v}`);
                } else {
                    prev.set(key, `${value}`);
                }
            };
            return prev;
        };
        if (setSearchParams instanceof URLSearchParams) fun(setSearchParams);
        else setSearchParams(fun);
        cb?.();
    },

    dateToUTCDate(date: Date) {
        return new Date(date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds());
    },

    formatToApiDate(date: Date, noTime: boolean = false, toUtc: boolean = true) {
        if (!date) return;
        if (toUtc) date = Utils.dateToUTCDate(date);
        const year = date.getFullYear();
        const day = ("" + date.getDate()).padStart(2, "0");
        const month = ("" + (date.getMonth() + 1)).padStart(2, "0");
        if (noTime) return `${year}-${month}-${day}`;
        const hours = ("" + date.getHours()).padStart(2, "0");
        const minutes = ("" + date.getMinutes()).padStart(2, "0");
        const seconds = ("" + date.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    buildSelectNoseurComponentProps({ disabled, title, type, name, placeholder, editable, options, value, extras }: FormControls, urlSearchParams?: URLSearchParams | NoseurObject<any>, setSearchParams?: SetURLSearchParams | URLSearchParams, cb?: Function, queryKeyName?: string, onSearch?: Function, onFormInput?: (name: string, value: any, search?: boolean) => any) {
        if (queryKeyName === undefined) queryKeyName = name;

        let defaultValue: any;
        const isSearchParams = !!urlSearchParams?.get;
        if (type === "date_range") {
            const fromDate = (isSearchParams
                ? defaultValue = urlSearchParams.get(`${queryKeyName!}_from`) ?? value
                : urlSearchParams && (`${queryKeyName!}_from` in urlSearchParams) && !!(urlSearchParams as any)[`${queryKeyName!}_from`] ? (urlSearchParams as any)[`${queryKeyName!}_from`] : value);
            const toDate = (isSearchParams
                ? defaultValue = urlSearchParams.get(`${queryKeyName!}_to`) ?? value
                : urlSearchParams && (`${queryKeyName!}_to` in urlSearchParams) && !!(urlSearchParams as any)[`${queryKeyName!}_to`] ? (urlSearchParams as any)[`${queryKeyName!}_to`] : value);
            defaultValue = [];
            if (!!fromDate) defaultValue.push(new Date(fromDate));
            if (!!toDate) defaultValue.push(new Date(toDate));
        } else {
            defaultValue = (isSearchParams
                ? defaultValue = urlSearchParams.get(queryKeyName) ?? value
                : urlSearchParams && (queryKeyName in urlSearchParams) && !!(urlSearchParams as any)[queryKeyName] ? (urlSearchParams as any)[queryKeyName] : value);
        }

        const inputProps: any = {
            name,
            fill: true,
            placeholder,
            defaultValue,
            style: { borderRadius: 0 },
            disabled: (disabled === true),
            onInputComplete: (e: string) => {
                onSearch && onSearch();
                onFormInput && onFormInput(queryKeyName!, e);
                if (!setSearchParams) return;
                Utils.updateSearchParams(queryKeyName!, e || undefined, setSearchParams, cb);
            },
        };
        if (type === "date" || type === "datetime" || type === "date_range") {
            inputProps.highlightToday = inputProps.highlightDatesInRange = true;
            if (type === "datetime") inputProps.disableToDate = DateHelper.addDays(new Date(), -1);
            if (type === "date_range") {
                inputProps.sticky = true;
                inputProps.selectionMode = DateTimePickerSelectionMode.RANGE;
                inputProps.leftLayout = DateTimePickerLayout.FINE_LEFT_LAYOUT;
                inputProps.selectedDates = defaultValue;
            } else {
                inputProps.selectedDates = defaultValue ? [(typeof defaultValue === "string" ? new Date(defaultValue) : defaultValue)] : undefined;
            }
            inputProps.onSelectDate = (options: DateTimePickerEvent) => {
                onSearch && onSearch();
                onFormInput && onFormInput(queryKeyName!, options);
                if (!setSearchParams) return;
                if (type === "date_range") {
                    Utils.updateSearchParams(`${queryKeyName!}_to`, (options.toDate ? Utils.formatToApiDate(options.toDate) : undefined), setSearchParams, cb);
                    Utils.updateSearchParams(`${queryKeyName!}_from`, (options.fromDate ? Utils.formatToApiDate(options.fromDate) : undefined), setSearchParams, cb);
                    return;
                }
                Utils.updateSearchParams(queryKeyName!, (options.selectedDate ? `${options.selectedDate}` : undefined), setSearchParams, cb);
            };
            inputProps.onClear = () => {
                if (!setSearchParams) return;
                if (type === "date_range") {
                    Utils.updateSearchParams(`${queryKeyName!}_to`, undefined, setSearchParams, cb);
                    Utils.updateSearchParams(`${queryKeyName!}_from`, undefined, setSearchParams, cb);
                    return;
                }
                Utils.updateSearchParams(queryKeyName!, undefined, setSearchParams, cb);
            };
        }
        if (type === "dropdown") {
            inputProps.options = options;
            inputProps.editable = editable;
            inputProps.onInputComplete = undefined;
            inputProps.optionMap = extras?.optionMap;
            if (editable) {
                inputProps.cleareable = extras?.clearable ?? true;
                inputProps.onDeSelectOption = (_: any) => {
                    if (!setSearchParams) return;
                    Utils.updateSearchParams(queryKeyName!, undefined, setSearchParams, cb);
                };
                inputProps.onSearch = (e: any, dropdownManageRef?: DropdownManageRef) => {
                    onSearch && onSearch();
                    onFormInput && onFormInput(queryKeyName!, { manageRef: dropdownManageRef, value: e.target.value }, true);
                    dropdownManageRef?.showDropDown(e);
                };
            }
            inputProps.onSelectOption = (option: any) => {
                onSearch && onSearch();
                if (onFormInput && !onFormInput(queryKeyName!, option.value)) {
                    return false;
                }
                extras?.onSelectOptions?.(option);
                if (setSearchParams) Utils.updateSearchParams(queryKeyName!, option.value ?? undefined, setSearchParams, cb);
                return true;
            };
            inputProps.selectedOptionIndex = options?.findIndex(c => c.value === defaultValue || c.label === defaultValue);
            if (inputProps.selectedOptionIndex === -1 || inputProps.selectedOptionIndex === undefined) inputProps.defaultInputValue = defaultValue;
        }
        if (type === "checkbox") {
            inputProps.noStyle = false;
            delete inputProps.onInputComplete;
            inputProps.label = title ?? placeholder;
            inputProps.scheme = Schemes.OMNEHOST;
            inputProps.defaultChecked = defaultValue === true;
            inputProps.onChange = (e: any) => {
                onSearch && onSearch();
                onFormInput && onFormInput(queryKeyName!, e.checked);
            }
        }
        return inputProps;
    },

    selectNoseurComponent(name?: string) {
        if (name === "email") return EmailInput;
        if (name === "checkbox") return Checkbox;
        if (name === "dropdown") return Dropdown;
        if (name === "phone") return NumberInput;
        if (name === "number") return NumberInput;
        if (name === "datetime") return DateTimeInput;
        if (name === "textarea") return TextAreaInput;
        if (name?.startsWith("date")) return DateInput;
        if (name === "password") return ComposedPassword;
        return TextInput;
    },

    getSetting(key: string, fallback?: any) {
        return Utils._SETTINGS[key] ?? fallback;
    },

    updateSettings(key: string, value: any) {
        Utils._SETTINGS[key] = value;
        CacheManager.put("omnehost.sts", Utils._SETTINGS);
    },

    numberWithNth(d: any) {
        let nth = "nth";
        d = parseInt(d);
        switch (d % 10) {
            case 1: nth = "st"; break;
            case 2: nth = "nd"; break;
            case 3: nth = "rd"; break;
            default: nth = "th"; break;
        }
        return `${d}${nth}`;
    },

    dateIsToday(date: Date) {
        return (new Date(date.valueOf()).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0));
    },

    dateIsYesterday(date: Date) {
        const yesterDate = new Date();
        yesterDate.setDate(yesterDate.getDate() - 1);
        return (new Date(date.valueOf()).setHours(0, 0, 0, 0) === yesterDate.setHours(0, 0, 0, 0));
    },

    //March 21st
    formatToShortDate(dateStr: string | Date) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.toLocaleString('default', { day: '2-digit' }).padStart(2, "0");
        return `${month} ${Utils.numberWithNth(day)}`;
    },

    //March 21st 2023 04:91 PM GMT+0
    formatToFineDate(dateStr: string | Date, skipTimezone: boolean = false, noTime?: boolean) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.toLocaleString('default', { day: '2-digit' }).padStart(2, "0");
        const time = (noTime ? "" : date.toLocaleTimeString('default', { hour12: true, hour: '2-digit', minute: '2-digit', timeZoneName: (skipTimezone ? undefined : 'short') }));
        return `${month} ${Utils.numberWithNth(day)}, ${date.getFullYear()} ${time}`;
    },

    // Thu 5th March 2021 01:20 PM GMT+1
    formatToFineDate1(dateStr: string | Date, skipTimezone: boolean = false, noTime?: boolean) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const day = date.toLocaleString('default', { day: 'numeric' });
        const month = date.toLocaleString('default', { month: 'long' });
        const weekday = date.toLocaleString('default', { weekday: 'short' });
        const time = (noTime ? "" : date.toLocaleTimeString('default', { hour12: true, hour: '2-digit', minute: '2-digit', timeZoneName: (skipTimezone ? undefined : 'short') }).toUpperCase());
        return `${weekday} ${Utils.numberWithNth(day)} ${month} ${date.getFullYear()} ${time}`;
    },

    // Thu 5th March 2021 01:20 PM
    formatToFineDate2(dateStr: string | Date) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const day = date.toLocaleString('default', { day: 'numeric' });
        const month = date.toLocaleString('default', { month: 'long' });
        const weekday = date.toLocaleString('default', { weekday: 'short' });
        const time = date.toLocaleTimeString('default', { hour12: true, hour: '2-digit', minute: '2-digit' }).toUpperCase();
        return `${weekday} ${Utils.numberWithNth(day)} ${month} ${date.getFullYear()} ${time}`;
    },

    // March 1st, 2024 | 01:31 AM GMT+1
    formatToFineDate3(dateStr: string | Date, skipTimezone: boolean = false) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.toLocaleString('default', { day: '2-digit' }).padStart(2, "0");
        const time = date.toLocaleTimeString('default', { hour12: true, hour: '2-digit', minute: '2-digit', timeZoneName: (skipTimezone ? undefined : 'short') }).toUpperCase();
        return `${month} ${Utils.numberWithNth(day)}, ${date.getFullYear()} | ${time}`;
    },

    // Mar 1st, 2024 | 01:31 AM
    formatToFineDate4(dateStr: string | Date) {
        const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
        const month = date.toLocaleString('default', { month: 'short' });
        const day = date.toLocaleString('default', { day: '2-digit' }).padStart(2, "0");
        const time = date.toLocaleTimeString('default', { hour12: true, hour: '2-digit', minute: '2-digit' }).toUpperCase();
        return `${month} ${day}, ${date.getFullYear()} ${time}`;
    },

    paymentMethodLogoMap(brand: string) {
        return {
            "jcb": Jcb,
            "amex": Amex,
            "visa": VisaCard,
            "payment_link": Bank,
            "discover": Discover,
            "union_pay": UnionPay,
            "mastercard": MasterCard,
            "dinner_club": DinnerClub,
        }[brand] ?? Unknown;
    },

    addSeparatorAtEveryDivisor(value: string, divisor: number, sep: string = " ") {
        const valueLength = value.length;
        return value.split("").map((ch, i) => ((i !== 0 && (i + 1) % divisor) === 0 && !(i > ((valueLength - i + 2) + divisor))) ? `${ch}${sep}` : ch).join("");
    },

    downloadBlob(content: string, filename: string, contentType: string = "text/csv;charset=utf-8;") {
        const blob = new Blob([content], { type: contentType });
        const url = URL.createObjectURL(blob);
        const pom = document.createElement('a');
        pom.href = url;
        pom.setAttribute('download', filename);
        pom.click();
    },

    escapeCharacters(message: string) {
        let messagePart = message.split("");
        for (let index = 0; index < messagePart.length; index++) {
            let char = messagePart[index];
            if ("`~!@#$%^&*()_+-={}[]<>?".includes(char)) {
                char = "\\" + char;
            }
            messagePart[index] = char;
        }
        return messagePart.join("");
    },

    resolveEnclosedValues(content: string,
        paramsMap: {
            replace: { open: string; close: string; fn?: (m: string, v?: string, ro?: string, rc?: string, lrp?: string, lrc?: string) => string; }
            search: { open: string; close: string; optionallyEnclosed?: boolean; excludeEnclosedChar?: string; optionalContent?: boolean; skipEscape?: boolean; },
        }[]) {
        let lastReplaceOpen: any;
        let lastReplaceClose: any;
        let resolvedContent = content;
        for (const params of paramsMap) {
            let { open: replaceOpen, close: replaceClose, fn } = params.replace;
            let { open: searchOpen, close: searchClose, optionallyEnclosed, excludeEnclosedChar, optionalContent, skipEscape } = params.search;
            let escapedSearchOpen = (skipEscape ? searchOpen : Utils.escapeCharacters(searchOpen));
            let escapedSearchClose = (skipEscape ? searchClose : Utils.escapeCharacters(searchClose));
            if (excludeEnclosedChar === undefined) excludeEnclosedChar = searchClose[searchClose.length - 1];
            let inBetweenExcludingSearch = `\\w&,."'+×÷=/-/-<>@$(~\`\\[\\]():;/? 	\n`;
            for (const ch of (excludeEnclosedChar ?? "").split("")) inBetweenExcludingSearch = inBetweenExcludingSearch.replace(ch, "");
            resolvedContent = resolvedContent.replace(new RegExp(escapedSearchOpen + (optionallyEnclosed ? "*" : "") + `([${inBetweenExcludingSearch}]${optionalContent ? "?" : ""})+` + escapedSearchClose, "g"), (m, _) => {
                let result, value = (m.replace(searchOpen, "")?.replace(searchClose, "") ?? "");
                if (searchClose === "\n") value = value.trim();
                if (fn) result = fn(m, value, replaceOpen, replaceClose, lastReplaceOpen, lastReplaceClose);
                lastReplaceOpen = replaceOpen;
                lastReplaceClose = replaceClose;
                if (result !== undefined) return result;
                return `${replaceOpen}${value}${replaceClose}`;
            });
        }
        return resolvedContent;
    },

    // TODO revisit and move to noseur
    markdownToHtml(content: string) {
        return Utils.MARKDOWN_CONVERTER.makeHtml(content);
    },

    containsAny(value: string, ops: string[]) {
        for (const op of ops) {
            if (value.includes(op)) return true;
        }
        return false;
    },

    buildPublicUrl(type: "TOURNAMENT" | "HOST" | "PROFILE" | "TEAM", id: string, suffix?: string) {
        let sep = "me";
        if (type === "TEAM") sep = "te";
        else if (type === "HOST") sep = "ho";
        else if (type === "TOURNAMENT") sep = "to";
        return `${process.env.REACT_APP_OMNEHOST_USERLAND_BASE_URL}/${sep}/${id}${suffix ?? ""}`;
    },

    downloadHtmlElement(element: HTMLElement, fileName: string) {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = (element as HTMLCanvasElement).toDataURL();
        link.click();
    },

    async getFileFromUrl(url: string, name: string, defaultType = 'image/jpeg') {
        const response = await fetch(url);
        const data = await response.blob();
        return new File([data], name, {
            type: data.type || defaultType,
        });
    },

    getResourceName(resource: IMember | ITeam = {} as any, fb?: string) {
        return (resource as any).username ?? (resource as any).name ?? fb ?? "";
    },

    getResourceImage(resource: IMember | ITeam = {} as any, fb?: string) {
        return (resource as any).profile_picture_url ?? (resource as any).logo_url ?? fb ?? "";
    },

    hasSpaceOrSpecial(str: string) {
        return /[^a-zA-Z0-9_-]/.test(str);
    },

};

export const Share = {

    twitter(message: any) {
        window.open(`https://twitter.com/intent/tweet?text=${message}`, "_blank");
    },

    whatsapp(message: any) {
        window.open(`https://wa.me/00000000000?text=${message}`, "_blank");
    },

    instagram(message: any) {
        window.open(`instagram://sharesheet?text=${message}`, "_blank");
    },

    facebook(message: any) {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${message}`, "_blank");
    },

}

export * from "./Encryptor";
export type { Encryptor } from "./Encryptor";
export { CacheManager } from "./CacheManager";
export const Schemes: {
    IDLE: Scheme;
    OMNEHOST: Scheme;
} = {
    IDLE: "idle" as Scheme,
    OMNEHOST: "omnehost" as Scheme,
}
